import React from "react";
import moment from "moment";
import { Input } from "semantic-ui-react";
import { type AxiosPromise } from "axios";
import { routes } from "utilities/routing/generatedRoutes";
import { RadioGroup } from "components/Atoms/inputs/RadioGroup";
import { EditSectionTemplate } from "components/MainSectionTemplate/EditSectionTemplate";
import CompanyModalPicker from "components/Picker/CompanyPicker/CompanyModalPicker";
import { BookkeepingInput, UsernameInput } from "components";
import ContactFromCompanyDropdown from "components/dropdowns/reactselect/ContactFromCompanyDropdown";
import CulturesInterfaceDropdown from "components/dropdowns/reactselect/CulturesInterfaceDropdown";
import ClientModalPicker from "components/Picker/Client/ClientModalPicker";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import { FormDatePicker } from "components/DatePicker/SingleDatePicker";
import NumberInput from "components/NumberInput";
import PercentageInput from "components/PercentageInput";
import { OptionalDatePicker } from "components/DatePicker/OptionalDatePicker";
import { HubSpotDealPanel } from "HubSpot/HubSpotDealPanel";
import { useLoaderData } from "react-router";

const ClientCreateApp = () => {
  const viewModel = useLoaderData<server.viewmodel.ClientNewFrontModel>();

  const { clientOnboarding, defaultInterfaceCulture, defaultSubscriptionType, defaultBillingCycle, defaultUserCount } = viewModel;

  const [currentSubscriptionType, setCurrentSubscriptionType] = React.useState<server.dto.SubscriptionTypes>(defaultSubscriptionType);
  const [selectedCompany, setSelectedCompany] = React.useState<server.dto.CompanySmall>(clientOnboarding?.company);
  const [selectedContact, setSelectedContact] = React.useState<server.dto.ContactSmallV2>(clientOnboarding?.contact);

  const [bookkeepingCode, setBookkeepingCode] = React.useState<string>("");

  const [username, setUsername] = React.useState<string>("");
  const [interfaceCulture, setInterfaceCulture] = React.useState<server.dto.Culture>(serverReference.interfaceCultures.find(i => i.value === defaultInterfaceCulture));

  const [templateClient, setTemplateClient] = React.useState<server.dto.ClientSmall>({
    clientId: 657,
    access: "activeFree",
    name: "Template account | DJ Agency",
    iconUrl: null,
    logoUrl: null
  });

  const [trialEnd, setTrialEnd] = React.useState<moment.Moment>(moment.utc().add(14, "days").startOf("day"));

  const [subscriptionStart, setSubscriptionStart] = React.useState<moment.Moment>(moment.utc().add(1, "month").startOf("month"));
  const [billingCycle, setBillingCycle] = React.useState<server.dto.BillingCycles>(defaultBillingCycle);
  const [internalUsers, setInternalUsers] = React.useState<number>(defaultUserCount);
  const [discount, setDiscount] = React.useState<number>(0);
  const [discountUntil, setDiscountUntil] = React.useState<moment.Moment | undefined>(null);

  const [linesOnInvoice, setLinesOnInvoice] = React.useState<string>("");

  const [duplicateAddressBook, setIsDuplicateAddressBook] = React.useState<boolean>(false);
  const [duplicatePlanning, setIsDuplicatePlanning] = React.useState<boolean>(false);
  const [duplicateWordTemplates, setIsDuplicateWordTemplates] = React.useState<boolean>(false);
  const [hsDealId, setHsDealId] = React.useState<number>(null);

  const discountError = currentSubscriptionType === "paid" && (discount < 0 || discount >= 1);

  const onSubmit = async () => {
    let request: AxiosPromise<server.dto.ApiResponseWrapper<number>>;
    if (currentSubscriptionType === "paid") {
      request = window.CoreApi.Client.CreateNewClientWithPaidSubscription({
        clientOnboardingId: clientOnboarding.clientOnboardingId,
        basicDetails: {
          hubSpotDealId: hsDealId,
          duplicatePlanning,
          duplicateWordTemplates,
          duplicateAddressBook,
          companyId: selectedCompany.companyId,
          bookkeepingCode,
          contactId: selectedContact.contactId,
          firstUsername: username,
          interfaceCulture: interfaceCulture.value,
          sourceClientId: templateClient.clientId,
        },
        subscriptionStart: subscriptionStart.toDate(),
        billingCycle,
        internalUsers,
        discountPercentage: discount,
        discountReason: linesOnInvoice || null,
        discountUntil: discountUntil?.toDate()
      });
    } else if (currentSubscriptionType === "trial") {
      request = window.CoreApi.Client.CreateNewClientWithTrial({
        clientOnboardingId: clientOnboarding.clientOnboardingId,
        basicDetails: {
          hubSpotDealId: hsDealId,
          duplicatePlanning,
          duplicateWordTemplates,
          duplicateAddressBook,
          companyId: selectedCompany.companyId,
          bookkeepingCode,
          contactId: selectedContact.contactId,
          firstUsername: username,
          interfaceCulture: interfaceCulture.value,
          sourceClientId: templateClient.clientId,
        },
        trialEnd: trialEnd.toDate()
      });
    } else {
      alert("Unknown subscription type: " + currentSubscriptionType);
    }

    return request
  };


  return <EditSectionTemplate
    submit={onSubmit}
    redirectUrl={(itemId) => routes.client_DetailAdminOne(itemId).absolute}
    sections={[{
      type: "regular",
      key: "company",
      title: RESX.Client.NewClient,
      fields: [{
        key: "company",
        label: RESX.Company.resxCompany,
        renderEditNew: () => <CompanyModalPicker
          modalTitle={RESX.Company.resxCompany}
          addressType="administrative"
          canCreateNew
          afterCreate="closeSidebar"
          nonInPageEditing={{
            onChange: (company) => setSelectedCompany(company),
            value: selectedCompany,
            validatorKey: "company",
            required: true,
          }}
        />
      }, {
        key: "bookkeepingCode",
        label: "Bookkeeping code",
        renderEditNew: () =>
          <BookkeepingInput
            value={bookkeepingCode}
            onChange={setBookkeepingCode}
            validationKey="bookkeepingCode"
          />
      }, {
        key: "contact",
        label: "First user",
        renderEditNew: () => <ContactFromCompanyDropdown
          value={selectedContact}
          onChange={setSelectedContact}
          companyId={selectedCompany?.companyId}
          config={{ isDisabled: selectedCompany == null }}
        />
      }, {
        key: "username",
        label: "Username",
        renderEditNew: () => <UsernameInput
          value={username}
          onChange={setUsername}
          validatorKey="username"
          noErrorText
        />
      }, {
        key: "language",
        label: "Language",
        renderEditNew: () => <CulturesInterfaceDropdown
          value={interfaceCulture}
          onChange={(culture) => setInterfaceCulture(culture)}
        />
      }, {
        key: "templateClient",
        label: "Template client",
        renderEditNew: () => <ClientModalPicker
          preferredClients={[]}
          canCreateNew={false}
          nonInPageEditing={{
            onChange: (client) => setTemplateClient(client),
            value: templateClient,
            required: true,
            validatorKey: "templateClient",
          }}
        />
      }, {
        key: "duplicate",
        label: "Duplicate",
        renderEditNew: () => <div className="flex-column gap-small">
          <div>
            <Checkbox
              label="Addressbook"
              checked={duplicateAddressBook}
              onChange={(_, checked) => setIsDuplicateAddressBook(checked)}
            />
          </div>
          <div>
            <Checkbox
              label="Planning"
              checked={duplicatePlanning}
              onChange={(_, checked) => setIsDuplicatePlanning(checked)}
            />
          </div>
          <div>
            <Checkbox
              label="Word templates"
              checked={duplicateWordTemplates}
              onChange={(_, checked) => setIsDuplicateWordTemplates(checked)}
            />
          </div>
        </div>
      }]
    }, {
      type: "regular",
      key: "subscription",
      title: "Subscription",
      fields: [{
        key: "type",
        label: "Type",
        renderEditNew: () => <RadioGroup
          orientation="vertical"
          options={[
            { label: "Trial", value: "trial" },
            { label: "Paid subscription", value: "paid" }
          ]}
          value={currentSubscriptionType}
          onChange={value => setCurrentSubscriptionType(value)}
        />
      }, {
        key: "trialEnd",
        label: "Trial end date",
        hidden: currentSubscriptionType !== "trial",
        renderEditNew: () => <FormDatePicker
          value={trialEnd}
          onChange={setTrialEnd}
          validatorKey="trialEndDate"
          minDate={moment.utc().startOf("day")}
          maxDate={moment.utc().add(10, "year")}
        />
      }, {
        key: "subscriptionStart",
        label: "Subscription start",
        hidden: currentSubscriptionType !== "paid",
        renderEditNew: () => <FormDatePicker
          value={subscriptionStart}
          onChange={setSubscriptionStart}
          validatorKey="subscriptionStartDate"
          minDate={moment.utc().subtract(1, "day").startOf("day")}
          maxDate={moment.utc().add(10, "year")}
        />
      }, {
        key: "billingCycle",
        label: "Billing cycle",
        hidden: currentSubscriptionType !== "paid",
        renderEditNew: () => <RadioGroup
          orientation="vertical"
          options={serverReference.billingCycles.map(cycle => ({ label: cycle.text, value: cycle.value }))}
          value={billingCycle}
          onChange={b => setBillingCycle(b)}
        />
      }, {
        key: "internalUsers",
        label: "Internal users",
        hidden: currentSubscriptionType !== "paid",
        renderEditNew: () => <NumberInput
          value={internalUsers}
          onChanged={val => { setInternalUsers(val.value); }}
          decimalPoints={0}
          canBeNegative={false}
          width={50}
          minValue={1}
          maxValue={999}
          canBeEmpty={false}
        />
      }, {
        key: "discount",
        label: "Discount",
        hidden: currentSubscriptionType !== "paid",
        renderEditNew: () => <PercentageInput
          value={discount}
          onChange={setDiscount}
          error={discountError}
          canBeNegative={false}
          digitsAfterDecimalPoint={2}
          enablePercentageLabel
        />
      }, {
        key: "discountUntil",
        label: "Discount until",
        hidden: currentSubscriptionType !== "paid",
        renderEditNew: () => <OptionalDatePicker
          dontUpdateOnInputChange
          validatorKey="discountUntil"
          minDate={moment.utc().subtract(10, "year")}
          maxDate={moment.utc().add(10, "year")}
          value={discountUntil}
          onChange={setDiscountUntil}
          initialDateAfterToggleOn={moment.utc().add(1, "day").startOf("day")}
        />
      }, {
        key: "lineOnInvoice",
        label: "Line on invoice",
        hidden: currentSubscriptionType !== "paid",
        renderEditNew: () => <Input
          fluid
          placeholder="This will be shown on client invoice"
          value={linesOnInvoice}
          onChange={(_, { value }) => { setLinesOnInvoice(value); }}
          maxLength={serverReference.constants.client.maxDiscountReasonLength}
        />
      }]
    }, {
      key: "hubSpotDealPanel",
      type: "custom",
      render: () => <HubSpotDealPanel
        key="hubSpotDealPanel"
        type={{
          onChange: setHsDealId,
          value: hsDealId
        }}
      />
    }]}
  />
};

export default ClientCreateApp;
